import MenuIcon from '@/public/icons/gnb_menu.svg';
import { IconButton } from '@/components/@IntranetLibrary/GNB/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useGNBEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { GNBEventName } from '@/constant/gtm';
import { Box, Button, Grid, Link, Popover, Typography } from '@mui/material';
import {
  GNBMenuItem,
  GNBMenuTabItem,
  GNBMenuWrapper,
} from '@/components/@IntranetLibrary/GNB/GNBMenu/styles';
import CloseIcon from '@/public/icons/close_black.svg';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useMenu } from '@/hooks/apiHooks';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { IframeFlag } from '@/layout/Default';
import { MenuData, MenuResponse } from '@/types/api';
import { PAGE_URL } from '@/constant/url';
import StackWithDivider from '@/components/StackWithDivider';
import LinkMenuIcon from '@/public/icons/link_menu.svg';
import { useBottomSheetIframe } from '@/hooks/global-tools';
import type { Swiper as SwiperClass } from 'swiper/types';
import classNames from 'classnames';

type MenuSection = {
  title: string;
  menus: MenuData[];
};

const GNBMenu = () => {
  const gnbEventTracker = useGNBEventTracker();

  const myInfo = useAtomValue(MyInfoState);
  const { data: _rawData } = useMenu(myInfo);
  const [rawData, setRawData] = useState<MenuResponse>();
  const [menuData, setMenuData] = useState<MenuSection[]>([]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const scrollRef = useRef<HTMLDivElement>(null);
  const menuTabSwiperRef = useRef<SwiperClass | null>(null);
  const [activeMenuIndex, setActiveMenuIndex] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [isClick, setIsClick] = useState(false);
  const offsetTop = 48 + 62; // header: 48px, menuTab: 62px

  const { openBottomSheet } = useBottomSheetIframe();

  // rawData 세팅
  useEffect(() => {
    if (!_rawData) return;

    const appendIframeFlagIfInsa = (
      child: (typeof _rawData.data)[0]['childData'][0],
    ) =>
      child.name === '인사조회'
        ? { ...child, link: `${child.link}?${IframeFlag}=true` }
        : child;

    setRawData({
      ..._rawData,
      data: _rawData.data.map((data) => {
        // '인사조회'가 없으면 그대로 리턴
        if (!data.childData.some((child) => child.name === '인사조회'))
          return data;

        // 있으면 childData 수정해서 반환
        return {
          ...data,
          childData: data.childData.map(appendIframeFlagIfInsa),
        };
      }),
    });
  }, [_rawData]);

  // menuData 세팅
  useEffect(() => {
    if (!rawData) return;

    const menu = rawData.data
      .filter((item) => Boolean(item.isDisplay))
      .map((item) => {
        // childData가 있을 때만 처리
        const updatedChildData = item.childData?.map((child) =>
          // 'L.I.F.E' 링크 변경 예외 처리
          child.name === 'L.I.F.E'
            ? { ...child, link: PAGE_URL.LIFE_HTML }
            : child,
        );

        return {
          title: item.name,
          menus: updatedChildData ?? item.childData ?? [],
        };
      });

    setMenuData(menu);
  }, [rawData]);

  // activeMenuIndex 변경 시 menu swiper slide
  useEffect(() => {
    menuTabSwiperRef.current?.slideTo(activeMenuIndex);
  }, [activeMenuIndex]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    gnbEventTracker(GNBEventName.clickMenu);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setActiveMenuIndex(0); // 메뉴를 닫을 때 첫 번째 탭으로 초기화
  };

  const handleClickMenuTab = (index: number) => {
    const target = sectionRefs.current?.[index];
    if (!target) return;

    setIsClick(true);
    setActiveMenuIndex(index);
    scrollRef.current?.scrollTo?.({
      top: Math.max(target.offsetTop - offsetTop, 0),
      behavior: 'auto',
    });
  };

  const onScroll: React.UIEventHandler<HTMLDivElement> = (event) => {
    if (
      isClick ||
      !event.target ||
      !sectionRefs.current ||
      sectionRefs.current.length === 0
    ) {
      setIsClick(false);
      return;
    }

    const scrollY =
      ((event.target as HTMLDivElement).scrollTop ?? 0) + offsetTop;

    sectionRefs.current.forEach((ref, index) => {
      if (ref) {
        const offsetTop = ref.offsetTop;
        const offsetBottom = ref.offsetTop + ref.clientHeight;

        if (scrollY >= offsetTop && scrollY < offsetBottom) {
          setActiveMenuIndex(index);
        }
      }
    });
  };

  const onClickMenu = (area_name: string, button_text: string) => {
    gnbEventTracker(GNBEventName.clickMore, { area_name, button_text });
    handleClose();
  };

  return (
    <>
      <IconButton id={'header-menu-button'} onClick={handleOpen}>
        <MenuIcon />
      </IconButton>

      {/* 사이트 메뉴 */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 40, horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { backgroundColor: 'transparent' } },
        }}
      >
        <GNBMenuWrapper>
          <Box className={'header'}>
            <Box
              sx={{ lineHeight: '24px', fontWeight: '500', fontSize: '16px' }}
            >
              App Box
            </Box>
            <Button onClick={handleClose}>
              <CloseIcon />
            </Button>
          </Box>
          <Box ref={scrollRef} className={'content'} onScroll={onScroll}>
            <Box className={'menu-tab'}>
              <Swiper
                slidesPerView={'auto'}
                draggable={false}
                simulateTouch={true}
                grabCursor={true}
                onSwiper={(swiper) => (menuTabSwiperRef.current = swiper)}
                style={{ padding: '0 16px' }}
                initialSlide={activeMenuIndex}
                onClick={(swiper) => {
                  handleClickMenuTab(swiper.clickedIndex);
                }}
              >
                {menuData.map((item, index) => (
                  <SwiperSlide
                    key={`menu-${index}`}
                    style={{ width: 'auto', padding: '12px 4px' }}
                    className={classNames({
                      active: index === activeMenuIndex,
                    })}
                  >
                    <GNBMenuTabItem>{item.title}</GNBMenuTabItem>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
            {menuData.map((item, index) => {
              return (
                <StackWithDivider
                  key={`menu-${item.title}`}
                  component={'section'}
                  gap={'12px'}
                  sx={{ marginY: '4px', padding: '20px' }}
                  dividerProps={{ sx: { borderColor: 'black' } }}
                  ref={(el) => {
                    sectionRefs.current[index] = el as HTMLDivElement;
                  }}
                >
                  <Typography fontSize={'14px'} color={'var(--dk-text-secondary, #888D96)'}>
                    {item.title}
                  </Typography>
                  <Grid container rowSpacing={'24px'} columnSpacing={'16px'}>
                    {item.menus.map((menu) => {
                      const { isDisplay, action, name, link = '' } = menu;
                      if (!isDisplay) return null;

                      const isLink = action !== 'modal' && action !== 'layer';

                      return (
                        <GNBMenuItem key={name} size={{ xs: 6 }}>
                          {isLink ? (
                            <Link
                              href={link}
                              target={action === 'newTab' ? '_blank' : '_self'}
                              rel={'noopener noreferrer'}
                              onClick={() => onClickMenu(item.title, name)}
                            >
                              {name}
                              {action === 'newTab' && (
                                <LinkMenuIcon width={20} height={20} />
                              )}
                            </Link>
                          ) : (
                            <Button
                              disableRipple
                              onClick={() => {
                                onClickMenu(item.title, name);
                                openBottomSheet({
                                  title: name,
                                  url: link,
                                  iframeId: name,
                                  isRn: false,
                                  isExpandable: true,
                                });
                              }}
                            >
                              {name}
                            </Button>
                          )}
                        </GNBMenuItem>
                      );
                    })}
                  </Grid>
                </StackWithDivider>
              );
            })}
          </Box>
        </GNBMenuWrapper>
      </Popover>
    </>
  );
};

export default GNBMenu;
